import React, { useEffect, useRef, useState } from 'react'
import { Card, Tabs, Table, Tag, Button, Space, Segmented, Progress, Statistic, Row, Col, InputNumber, Select, Modal, Descriptions, List, Switch, message } from 'antd'
import { PlayCircleOutlined, PauseCircleOutlined, StopOutlined, FieldTimeOutlined } from '@ant-design/icons'
import api from '../api'

const PLATFORM_COLORS = {
  douyin: '#ff4d4f', kuaishou: '#fa8c16', xiaohongshu: '#eb2f96',
  weibo: '#1890ff', bilibili: '#13c2c2', shipinhao: '#52c41a', jinritoutiao: '#722ed1'
}

const PLATFORM_NAMES = {
  douyin: '抖音', kuaishou: '快手', xiaohongshu: '小红书',
  weibo: '微博', bilibili: 'B站', shipinhao: '视频号', jinritoutiao: '头条'
}

const STATUS_CONFIG = {
  running: { color: 'processing', label: '运行' },
  waiting: { color: 'default', label: '等待' },
  offline: { color: 'warning', label: '掉线' },
  no_work: { color: 'error', label: '无作品' },
  paused: { color: 'orange', label: '暂停' },
  failed: { color: 'error', label: '失败' },
  completed: { color: 'success', label: '完成' },
  stopped: { color: 'red', label: '终止' }
}

const PREVIEW_TASKS = []

function TaskListPage() {
  const [threadMode, setThreadMode] = useState('single')
  const [executionStrategy, setExecutionStrategy] = useState('cycle')
  const [minAccountIntervalSeconds, setMinAccountIntervalSeconds] = useState(30)
  const [showPublishBrowser, setShowPublishBrowser] = useState(false)
  const [tasks, setTasks] = useState(PREVIEW_TASKS)
  const [running, setRunning] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const runningTasks = tasks.filter(item => !['completed', 'stopped'].includes(item.status))
  const completedTasks = tasks.filter(item => ['completed', 'stopped'].includes(item.status))

  const refreshTasks = () => {
    api.listPublishTasks().then(result => {
      const nextTasks = Array.isArray(result.tasks) && result.tasks.length > 0 ? result.tasks : PREVIEW_TASKS
      setTasks(nextTasks)
      setSelectedTask(current => current ? nextTasks.find(item => item.id === current.id) || current : current)
      setRunning(Boolean(result.running))
      setThreadMode(result.config?.threadMode || 'single')
      setExecutionStrategy(result.config?.executionStrategy || 'cycle')
      setMinAccountIntervalSeconds(Number(result.config?.minAccountIntervalSeconds || 30))
      setShowPublishBrowser(Boolean(result.config?.showPublishBrowser))
    })
  }

  useEffect(() => {
    refreshTasks()
    const off = api.on('publish-tasks-updated', refreshTasks)
    return off
  }, [])

  useEffect(() => {
    const timer = setInterval(() => {
      api.refreshPublishTaskStatus().then(refreshTasks).catch(() => {})
    }, 30000)
    return () => clearInterval(timer)
  }, [])

  const updateConfig = (patch) => {
    const next = { threadMode, executionStrategy, minAccountIntervalSeconds, showPublishBrowser, ...patch }
    setThreadMode(next.threadMode)
    setExecutionStrategy(next.executionStrategy)
    setMinAccountIntervalSeconds(next.minAccountIntervalSeconds)
    setShowPublishBrowser(Boolean(next.showPublishBrowser))
    api.updatePublishConfig(next).then(refreshTasks)
  }

  const startAll = () => api.startPublishTasks().then(result => {
    if (result.success) message.success(`已开始调度 ${result.queued_count || 0} 个任务`)
    else message.error(result.error || '全部开始失败')
    return refreshTasks()
  })
  const startOne = (taskId) => api.startPublishTask(taskId).then(result => {
    if (result.success) message.success('已开始此任务')
    else message.error(result.error || '开始任务失败')
    return refreshTasks()
  })
  const pauseAll = () => api.pauseAllPublishTasks().then(refreshTasks)
  const createDryRunTasks = () => api.createPreviewPublishTasks({ type: '视频', work_count: 3 }).then(refreshTasks)
  const updateTaskStatus = (taskId, status) => api.updatePublishTaskStatus(taskId, status).then(refreshTasks)
  const refreshStatus = () => api.refreshPublishTaskStatus().then(refreshTasks)
  const updateTaskBrowser = (taskId, visible) => api.setPublishBrowserVisibility({ task_id: taskId, visible }).then(refreshTasks)
  const updateAllTaskBrowser = (visible) => {
    setShowPublishBrowser(visible)
    api.updatePublishConfig({ threadMode, executionStrategy, minAccountIntervalSeconds, showPublishBrowser: visible })
      .then(() => api.setPublishBrowserVisibility({ visible, all: true }))
      .then(refreshTasks)
  }

  const getCompletedWorkCount = (task) => {
    const directCount = Number(task?.completed_work_count ?? task?.completedWorkCount)
    if (Number.isFinite(directCount) && directCount >= 0) return directCount
    const records = Array.isArray(task?.publish_records) ? task.publish_records : []
    return records.filter(item => ['success', 'dry_run'].includes(item?.status)).length
  }

  const getWorkCount = (task) => {
    const directCount = Number(task?.work_count ?? task?.workCount)
    if (Number.isFinite(directCount) && directCount > 0) return directCount
    const mediaCount = Array.isArray(task?.media_paths) ? task.media_paths.length : 0
    return Math.max(mediaCount, 1)
  }

  const columns = [
    { title: '任务编号', dataIndex: 'id', width: 150, fixed: 'left' },
    {
      title: '平台名称', dataIndex: 'platform', width: 100,
      render: value => <Tag color={PLATFORM_COLORS[value]}>{PLATFORM_NAMES[value] || value}</Tag>
    },
    { title: '账号名', dataIndex: 'account', width: 140 },
    { title: '任务类型', dataIndex: 'type', width: 90, render: value => <Tag>{value}</Tag> },
    { title: '任务进度', width: 110, render: (_, record) => `${getCompletedWorkCount(record)}/${getWorkCount(record)}` },
    {
      title: '任务状态', dataIndex: 'status', width: 100,
      render: value => <Tag color={STATUS_CONFIG[value]?.color}>{STATUS_CONFIG[value]?.label || value}</Tag>
    },
    { title: '失败/提示原因', dataIndex: 'fail_reason', width: 220, render: value => value || '-' },
    { title: '完成比例', dataIndex: 'progress', width: 160, render: value => <Progress percent={value} size="small" /> },
    { title: '今日发布数', dataIndex: 'todayCount', width: 110, render: value => `${value} 条` },
    {
      title: '浏览器', dataIndex: 'show_publish_browser', width: 100,
      render: (value, record) => <Switch size="small" checked={Boolean(value)} checkedChildren="显示" unCheckedChildren="隐藏" onClick={(checked, event) => { event.stopPropagation(); updateTaskBrowser(record.id, checked) }} />
    },
    {
      title: '操作', width: 240, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {!['completed', 'stopped', 'failed', 'no_work'].includes(record.status) && <Button size="small" type="primary" icon={<PlayCircleOutlined />} onClick={event => { event.stopPropagation(); startOne(record.id) }}>开始</Button>}
          {record.status === 'paused' ? <Button size="small" icon={<PlayCircleOutlined />} onClick={event => { event.stopPropagation(); updateTaskStatus(record.id, 'waiting') }}>继续</Button> : <Button size="small" icon={<PauseCircleOutlined />} onClick={event => { event.stopPropagation(); updateTaskStatus(record.id, 'paused') }}>暂停</Button>}
          <Button size="small" danger icon={<StopOutlined />} onClick={event => { event.stopPropagation(); updateTaskStatus(record.id, 'stopped') }}>终止</Button>
        </Space>
      )
    }
  ]

  const [tableTab, setTableTab] = useState('running')

  const tableContainerRef = useRef(null)

  const renderTable = (data) => (
    <div ref={tableContainerRef} style={{ overflowX: 'auto' }}>
      <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      pagination={false}
        scroll={{ x: 1320 }}
        onRow={record => ({ onClick: () => setSelectedTask(record), style: { cursor: 'pointer' } })}
      />
    </div>
  )

  const totalTodayCount = Object.values(tasks.reduce((acc, item) => {
    acc[item.account_id || item.account] = Number(item.todayCount || 0)
    return acc
  }, {})).reduce((sum, value) => sum + value, 0)
  const taskMedia = selectedTask?.media_paths || []
  const taskRecords = selectedTask?.publish_records || []
  const taskScheduleTimes = selectedTask?.schedule_times || []
  const taskSteps = selectedTask?.publish_steps || []
  const renderRecordMaterial = (item) => {
    const material = item.material || {}
    const parts = []
    if (material.title) parts.push(`标题：${material.title}`)
    if (Array.isArray(material.topics) && material.topics.length > 0) parts.push(`话题：${material.topics.join(' ')}`)
    if (material.location) parts.push(`位置：${material.location}`)
    if (material.schedule_time) parts.push(`定时：${material.schedule_time}`)
    if (Array.isArray(material.comments) && material.comments.length > 0) parts.push(`评论：${material.comments.join(' / ')}`)
    return parts.length > 0 ? ` | ${parts.join(' | ')}` : ''
  }

  return (
    <div style={{ flex: 1, padding: 18, background: '#f5f7fb', overflow: 'auto' }}>
      <Card
        title="任务列表"
        extra={(
          <Space wrap>
            <Segmented value={threadMode} onChange={value => updateConfig({ threadMode: value })} options={[{ label: '单线程运行', value: 'single' }, { label: '三线程运行', value: 'triple' }]} />
            <Switch checked={showPublishBrowser} onChange={updateAllTaskBrowser} checkedChildren="全部显示浏览器" unCheckedChildren="全部隐藏浏览器" />
            <Button type="primary" icon={<PlayCircleOutlined />} onClick={startAll}>全部开始</Button>
            <Button icon={<PauseCircleOutlined />} onClick={pauseAll}>全部暂停</Button>
            <Button onClick={refreshStatus}>刷新状态</Button>
            <Button onClick={createDryRunTasks}>生成测试任务</Button>
          </Space>
        )}
      >
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space size="large" wrap>
            <span>执行方式</span>
            <Select
              value={executionStrategy}
              style={{ width: 260 }}
              onChange={value => updateConfig({ executionStrategy: value })}
              options={[
                { value: 'cycle', label: '循环执行：每账号发一条后切换下个等待账号' },
                { value: 'sequential', label: '顺序执行：一个账号发完再切换下个账号' }
              ]}
            />
            <span>同账号两条作品间隔</span>
            <InputNumber min={0} value={minAccountIntervalSeconds} onChange={value => updateConfig({ minAccountIntervalSeconds: value || 0 })} />
            <span>秒</span>
            <Tag color={running ? 'processing' : 'default'}>{running ? '调度中' : '已停止'}</Tag>
          </Space>
        </Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}><Card size="small"><Statistic title="运行中任务" value={runningTasks.length} prefix={<FieldTimeOutlined />} /></Card></Col>
          <Col span={6}><Card size="small"><Statistic title="已完成任务" value={completedTasks.length} /></Card></Col>
          <Col span={6}><Card size="small"><Statistic title="运行模式" value={threadMode === 'single' ? '单线程' : '三线程'} /></Card></Col>
          <Col span={6}><Card size="small"><Statistic title="今日发布数" value={totalTodayCount} suffix="条" /></Card></Col>
        </Row>
        <Segmented
          value={tableTab}
          onChange={setTableTab}
          options={[
            { label: `正在运行的任务 ${runningTasks.length}`, value: 'running' },
            { label: `已完成的任务 ${completedTasks.length}`, value: 'completed' }
          ]}
          style={{ marginBottom: 12 }}
        />
        {tableTab === 'running' ? renderTable(runningTasks) : renderTable(completedTasks)}
      </Card>
      <Modal title="任务详情" open={Boolean(selectedTask)} onCancel={() => setSelectedTask(null)} footer={null} width={760}>
        {selectedTask && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="任务编号">{selectedTask.id}</Descriptions.Item>
              <Descriptions.Item label="账号名">{selectedTask.account}</Descriptions.Item>
              <Descriptions.Item label="平台">{PLATFORM_NAMES[selectedTask.platform] || selectedTask.platform}</Descriptions.Item>
              <Descriptions.Item label="状态">{STATUS_CONFIG[selectedTask.status]?.label || selectedTask.status}</Descriptions.Item>
              <Descriptions.Item label="浏览器显示">{selectedTask.show_publish_browser ? '显示' : '隐藏'}</Descriptions.Item>
              <Descriptions.Item label="任务进度">{getCompletedWorkCount(selectedTask)}/{getWorkCount(selectedTask)}</Descriptions.Item>
              <Descriptions.Item label="今日发布数" span={2}>{selectedTask.todayCount || 0} 条</Descriptions.Item>
              <Descriptions.Item label="失败/提示原因" span={2}>{selectedTask.fail_reason || '-'}</Descriptions.Item>
              <Descriptions.Item label="最近结果" span={2}>{selectedTask.last_result || '-'}</Descriptions.Item>
            </Descriptions>
            <Card size="small" title="计划发布作品">
              <List
                size="small"
                dataSource={taskMedia.length > 0 ? taskMedia : ['暂无作品路径']}
                renderItem={(item, index) => <List.Item>{index + 1}. {item} | 定时时间点：{taskScheduleTimes[index] || '立即发布'}</List.Item>}
              />
            </Card>
            <Card size="small" title="计划时间点">
              <List
                size="small"
                dataSource={taskScheduleTimes.length > 0 ? taskScheduleTimes : ['暂无计划时间点']}
                renderItem={(item, index) => <List.Item>{index + 1}. {item}</List.Item>}
              />
            </Card>
            <Card size="small" title="自动化步骤">
              <List
                size="small"
                dataSource={taskSteps.length > 0 ? taskSteps : [{ message: '暂无自动化步骤' }]}
                renderItem={item => <List.Item>{item.time || '-'} | {item.status || '-'} | {item.message || '-'}</List.Item>}
              />
            </Card>
            <Card size="small" title="执行记录">
              <List
                size="small"
                dataSource={taskRecords.length > 0 ? taskRecords : [{ result: '暂无执行记录' }]}
                renderItem={item => <List.Item>{item.time || '-'} | 第 {item.work_index || '-'} 条 | {item.status || '-'} | {item.media_path || '-'} | {item.result || '-'}{renderRecordMaterial(item)}</List.Item>}
              />
            </Card>
          </Space>
        )}
      </Modal>
    </div>
  )
}

export default TaskListPage
